/**
 * CalendarBlockEditDialog — Iter 84.
 *
 * Click an existing "Unavailable" strip to edit it: change start/end dates
 * with date pickers, edit the reason, or remove the block entirely.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Trash2, Save } from "lucide-react";

export default function CalendarBlockEditDialog({ open, car, range, onCancel, onSave, onDelete, busy }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open && range) {
      setStart(String(range.start || "").slice(0, 10));
      setEnd(String(range.end || "").slice(0, 10));
      setReason(range.reason || "");
    }
  }, [open, range]);

  const valid = start && end && end > start;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="cal-block-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit blocked dates</DialogTitle>
          <DialogDescription>
            {car && (
              <>
                <span className="font-medium text-neutral-900">{car.make} {car.model}</span> — adjust the dates or reason,
                or remove the block to make the car bookable again.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Blocked from</Label>
            <Input
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setStart(e.target.value)}
              className="rounded-lg mt-1"
              data-testid="cal-block-edit-start"
            />
          </div>
          <div>
            <Label className="text-xs">Until (exclusive)</Label>
            <Input
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              className="rounded-lg mt-1"
              data-testid="cal-block-edit-end"
            />
          </div>
        </div>
        {!valid && start && end && (
          <p className="text-xs text-rose-600" data-testid="cal-block-edit-invalid">End date must be after the start date.</p>
        )}
        <Input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional) — e.g. service, vacation"
          className="rounded-lg"
          data-testid="cal-block-edit-reason"
        />

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={onDelete}
            disabled={busy}
            className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            data-testid="cal-block-edit-delete"
          >
            {busy === "delete" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Remove block
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel} disabled={!!busy} className="rounded-full" data-testid="cal-block-edit-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => onSave({ start, end, reason: reason.trim() })}
              disabled={!!busy || !valid}
              className="rounded-full"
              data-testid="cal-block-edit-save"
            >
              {busy === "save" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
